import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Button, Form, InputGroup } from 'react-bootstrap';
import Select from 'react-select';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ManageTournamentsFixturesTable from '../../Common/ManageTournamentsFixturesTable';
import ShimmerLoader from '../../Common/ShimmerLoader';
import { DeleteModal } from '../../Common/DeleteModal';
import { 
  getAllTournamentsForAdmin,
  ManageTournament,
  getTournamentSeasons,
  getAllTournamentSeasons,
  deleteTournament,
} from '../../api/tournament/manageTournamentService';
import { hasAnyPermission } from '../../utils/roleBasedAccess';
import { useTournamentSports } from '../../hooks/useTournamentSports';
import '../../Common/ManageTournamentsFixturesTable.css';

const ManageTournamentsFixturesPage = () => {
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  
  // State management
  const [tournaments, setTournaments] = useState<ManageTournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [tournamentToDelete, setTournamentToDelete] = useState<ManageTournament | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingTournament, setIsDeletingTournament] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState(false);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sportsFilter, setSportsFilter] = useState('');
  
  // Dynamic sports options state
  const [sportsOptions, setSportsOptions] = useState<Array<{ value: string; label: string }>>([
    { value: '', label: 'All Sports' }
  ]);
  const { options: catalogueSportOptions } = useTournamentSports();
  
  // Use refs to track if sports have been loaded (to avoid circular dependencies)
  const sportsLoadedRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const isLoadingRef = useRef(false); // Track if API call is in progress
  const hasInitialFiltersSetRef = useRef(false); // Track if filters have been set by user (not initial state)

  // Check if user has access
  const userData = {
    ...user,
    subRole: role,
    permissions: permissions,
  };

  const canAccessFixtures = hasAnyPermission(userData, [
    'tournament.admin',
    'tournament.manage',
    'tournament.view',
    'tournament.read',
    'tournament.tournaments.view',
    'tournament.tournaments.manage',
    'tournament.fixtures.manage',
  ]);
  const canCreateTournament = hasAnyPermission(userData, [
    'tournament.admin',
    'tournament.manage',
    'tournament.tournaments.manage',
  ]);

  // Helper function to determine tournament status for sorting
  const getTournamentStatusForSorting = (tournament: ManageTournament): 'ongoing' | 'upcoming' | 'completed' | 'cancelled' => {
    // First, check if tournament is cancelled (highest priority check)
    if (tournament.status === 'cancelled' || tournament.cancellationInfo) {
      return 'cancelled';
    }
    
    // Prioritize API status field if it matches our expected values
    const apiStatus = (tournament.status || '').toLowerCase().trim();
    if (apiStatus === 'ongoing' || apiStatus === 'started' || apiStatus === 'live') {
      return 'ongoing';
    }
    if (apiStatus === 'upcoming' || apiStatus === 'pending' || apiStatus === 'scheduled') {
      return 'upcoming';
    }
    if (apiStatus === 'completed' || apiStatus === 'ended' || apiStatus === 'finished') {
      return 'completed';
    }
    
    // Fallback to date-based calculation if API status doesn't match expected values
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    
    const startDate = new Date(tournament.startDate);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(tournament.endDate);
    endDate.setHours(23, 59, 59, 999); // Set to end of day
    
    // Ongoing: current date is between start and end date
    if (today >= startDate && today <= endDate) {
      return 'ongoing';
    }
    
    // Upcoming: start date is in the future
    if (today < startDate) {
      return 'upcoming';
    }
    
    // Completed: end date is in the past
    return 'completed';
  };

  // Helper function to sort tournaments by status priority: ongoing > upcoming > completed > cancelled
  const sortTournamentsByStatus = (tournaments: ManageTournament[]): ManageTournament[] => {
    if (!tournaments || tournaments.length === 0) {
      return tournaments;
    }
    
    const statusPriority: { [key: string]: number } = {
      'ongoing': 1,
      'upcoming': 2,
      'completed': 3,
      'cancelled': 4  // Cancelled at the bottom
    };
    
    // Create a copy to avoid mutating the original array
    const sorted = [...tournaments].sort((a, b) => {
      const statusA = getTournamentStatusForSorting(a);
      const statusB = getTournamentStatusForSorting(b);
      
      // Get priority values (default to 999 for unknown statuses to put them at the end)
      const priorityA = statusPriority[statusA] ?? 999;
      const priorityB = statusPriority[statusB] ?? 999;
      
      // First, sort by status priority
      const priorityDiff = priorityA - priorityB;
      if (priorityDiff !== 0) {
        return priorityDiff;
      }
      
      // If same status, sort by start date (earliest first for ongoing/upcoming, latest first for completed/cancelled)
      const dateA = new Date(a.startDate || 0).getTime();
      const dateB = new Date(b.startDate || 0).getTime();
      
      if (statusA === 'completed' || statusA === 'cancelled') {
        // For completed and cancelled tournaments, show most recent first
        return dateB - dateA;
      } else {
        // For ongoing and upcoming, show earliest first
        return dateA - dateB;
      }
    });
    
    // Debug logging to verify sorting
    console.log('Tournament sorting:', sorted.map(t => ({
      name: t.name,
      apiStatus: t.status,
      calculatedStatus: getTournamentStatusForSorting(t),
      startDate: t.startDate
    })));
    
    return sorted;
  };

  // Helper function to extract unique sports from tournaments array
  const extractSportsFromTournaments = useCallback((tournaments: ManageTournament[]) => {
    if (!tournaments || tournaments.length === 0) {
      return;
    }
    
    // Extract unique sports from tournaments
    const sportsSet = new Set(
      tournaments
        .map(tournament => tournament.sports)
        .filter(sport => sport && sport.trim() !== '') // Filter out empty/null sports
    );
    const uniqueSports = Array.from(sportsSet).sort(); // Sort alphabetically
    
    // Create options array with "All Sports" first
    const sportsOptionsArray = [
      { value: '', label: 'All Sports' },
      ...uniqueSports.map(sport => ({ value: sport, label: sport }))
    ];
    
    setSportsOptions(sportsOptionsArray);
    sportsLoadedRef.current = true; // Mark sports as loaded
  }, []);

  const loadAllTournaments = useCallback(async (page: number = 1, search: string = searchTerm, status: string = statusFilter, sports: string = sportsFilter) => {
    // Prevent duplicate calls
    if (isLoadingRef.current) {
      return;
    }
    
    try {
      isLoadingRef.current = true;
      setIsPageLoading(true);
      
      const response = await getAllTournamentsForAdmin(page, 10, search, status, sports);
      
      // Sort tournaments by status: ongoing > upcoming > completed
      const sortedTournaments = sortTournamentsByStatus(response.tournaments);
      
      setTournaments(sortedTournaments);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
      
      // Extract sports from the current response if sports options haven't been loaded yet
      // This avoids a separate API call just for sports
      if (!sportsLoadedRef.current) {
        // Only extract sports if we haven't loaded them yet
        extractSportsFromTournaments(response.tournaments);
      }
      
    } catch (error: any) {
      console.error('❌ Error loading tournaments:', error);
      throw error;
    } finally {
      isLoadingRef.current = false;
      setIsPageLoading(false);
      setLoading(false); // Set main loading state to false after API call completes
    }
  }, [searchTerm, statusFilter, sportsFilter, extractSportsFromTournaments]);

  // Function to fetch unique sports from all tournaments (only if needed)
  // This is a fallback if the initial load doesn't have enough tournaments to extract all sports
  const loadSportsOptions = useCallback(async () => {
    try {
      // Only fetch if sports haven't been loaded yet
      if (sportsLoadedRef.current) {
        return; // Sports already loaded, skip API call
      }
      
      // Fetch a large number of tournaments to get all unique sports
      const response = await getAllTournamentsForAdmin(1, 1000, '', '', '');
      
      if (response.tournaments && response.tournaments.length > 0) {
        extractSportsFromTournaments(response.tournaments);
      }
    } catch (error) {
      console.error('❌ Error loading sports options:', error);
      // Keep the default "All Sports" option if API fails
    }
  }, [extractSportsFromTournaments]);


  // Initial data load - only once on mount
  useEffect(() => {
    if (!canAccessFixtures) {
      setLoading(false);
      return;
    }
    
    if (initialLoadDoneRef.current) {
      return; // Already loaded
    }
    
    initialLoadDoneRef.current = true;
    
    // Initial load - no debounce
    loadAllTournaments(1, searchTerm, statusFilter, sportsFilter);
    
    // Set a delayed fallback to load sports if they weren't extracted from initial response
    // This ensures we get all sports even if the first 10 tournaments don't have all unique sports
    const timeoutId = setTimeout(() => {
      if (!sportsLoadedRef.current) {
        loadSportsOptions();
      }
    }, 2000); // Wait 2 seconds for initial load to complete
    
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Handle filter changes - separate useEffect to avoid re-triggering initial load
  // CRITICAL: Do NOT include access state in dependencies
  // because they change when Redux state loads, causing duplicate API calls
  useEffect(() => {
    // Early return if user doesn't have access (but don't include in deps to avoid re-triggers)
    if (!canAccessFixtures) {
      return;
    }
    
    if (!initialLoadDoneRef.current) {
      return; // Wait for initial load to complete
    }
    
    // CRITICAL FIX: Skip if this is the initial mount (filters are empty, which is the default state)
    // This prevents the filter useEffect from running on initial mount
    // Only run if filters have actually been set by the user
    if (!hasInitialFiltersSetRef.current) {
      // Check if any filter has a non-empty value (user has interacted)
      if (searchTerm || statusFilter || sportsFilter) {
        hasInitialFiltersSetRef.current = true;
      } else {
        return;
      }
    }
    
    // Debounce filter changes
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      loadAllTournaments(1, searchTerm, statusFilter, sportsFilter);
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, sportsFilter]); // Access state intentionally omitted to prevent duplicate calls

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadAllTournaments(page);
  };

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadAllTournaments(1, searchTerm, statusFilter, sportsFilter);
  };

  // Handle filter changes
  const handleFilterChange = (filterType: string, value: string) => {
    if (filterType === 'status') {
      setStatusFilter(value);
    } else if (filterType === 'sports') {
      setSportsFilter(value);
    }
    
    setCurrentPage(1);
    // Let the useEffect handle the API call when filters change
  };

  // Handle tournament actions
  const handleViewTournament = async (tournament: ManageTournament) => {
    try {
      // Get all seasons including DEFAULT
      const allSeasons = await getAllTournamentSeasons(tournament._id);
      
      // Filter SEASON type seasons (multi-season tournaments)
      const actualSeasons = allSeasons.filter((s: any) => s.seasonType === 'SEASON');
      
      // Find DEFAULT season (single-season tournaments)
      const defaultSeason = allSeasons.find((s: any) => s.seasonType === 'DEFAULT');
      
      // Open the seasons listing whenever the tournament has a DEFAULT or regular season.
      if (actualSeasons.length > 0 || defaultSeason) {
        navigate(`/manage-tournaments-fixtures/${tournament._id}/seasons`, {
          state: { 
            viewMode: true,
            selectedTournament: tournament,
            allTournaments: tournaments // Pass all tournaments to avoid duplicate API call
          }
        });
      } else {
        // No seasons at all - navigate to detail page without seasonId
        navigate(`/manage-tournaments-fixtures/${tournament._id}`, {
          state: { 
            viewMode: true,
            selectedTournament: tournament,
            allTournaments: tournaments // Pass all tournaments to avoid duplicate API call
          }
        });
      }
    } catch (error: any) {
      console.error('Error checking tournament seasons:', error);
      navigate(`/manage-tournaments-fixtures/${tournament._id}`, {
        state: { 
          viewMode: true,
          selectedTournament: tournament,
          allTournaments: tournaments // Pass all tournaments to avoid duplicate API call
        }
      });
    }
  };

  const handleEditTournament = async (tournament: ManageTournament) => {
    try {
      // Get all seasons including DEFAULT
      const allSeasons = await getAllTournamentSeasons(tournament._id);
      
      // Filter SEASON type seasons (multi-season tournaments)
      const actualSeasons = allSeasons.filter((s: any) => s.seasonType === 'SEASON');
      
      // Find DEFAULT season (single-season tournaments)
      const defaultSeason = allSeasons.find((s: any) => s.seasonType === 'DEFAULT');
      
      // Open the seasons listing whenever the tournament has a DEFAULT or regular season.
      if (actualSeasons.length > 0 || defaultSeason) {
        navigate(`/manage-tournaments-fixtures/${tournament._id}/seasons`, {
          state: { 
            isEditMode: true,
            selectedTournament: tournament,
            allTournaments: tournaments // Pass all tournaments to avoid duplicate API call
          }
        });
      } else {
        // No seasons at all - navigate to detail page without seasonId
        navigate(`/manage-tournaments-fixtures/${tournament._id}`, {
          state: { 
            isEditMode: true,
            selectedTournament: tournament,
            allTournaments: tournaments // Pass all tournaments to avoid duplicate API call
          }
        });
      }
    } catch (error: any) {
      console.error('Error checking tournament seasons:', error);
      navigate(`/manage-tournaments-fixtures/${tournament._id}`, {
        state: { 
          isEditMode: true,
          selectedTournament: tournament,
          allTournaments: tournaments // Pass all tournaments to avoid duplicate API call
        }
      });
    }
  };

  // Check if tournament can be deleted (not ongoing or completed)
  const canDeleteTournament = (tournament: ManageTournament): boolean => {
    const status = (tournament.status || '').toLowerCase();
    return status !== 'ongoing' && status !== 'completed';
  };

  const handleDeleteTournament = (tournament: ManageTournament) => {
    // Double check if deletion is allowed
    if (!canDeleteTournament(tournament)) {
      return;
    }

    setTournamentToDelete(tournament);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    if (isDeletingTournament) {
      return;
    }

    setShowDeleteModal(false);
    setTournamentToDelete(null);
    setDeleteError(null);
  };

  const handleConfirmDeleteTournament = async () => {
    if (!tournamentToDelete?._id) {
      return;
    }

    try {
      setIsDeletingTournament(true);
      setDeleteError(null);
      await deleteTournament(tournamentToDelete._id);
      await loadAllTournaments(currentPage);
      setShowDeleteModal(false);
      setTournamentToDelete(null);
      setDeleteError(null);
    } catch (error: any) {
      console.error('Error deleting tournament:', error);
      setDeleteError(error.message || 'Failed to delete tournament. Please try again.');
    } finally {
      setIsDeletingTournament(false);
    }
  };

  const handleAddNew = () => {
    navigate('/manage-tournaments/add', {
      state: { isAddMode: true }
    });
  };

  // Access denied for non-authorized users
  if (!canAccessFixtures) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <h3>Access Denied</h3>
            <p>You don't have permission to access this page.</p>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Dashboard', onClick: () => navigate('/dashboard') },
    { label: 'Manage Tournaments', active: true }
  ];

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        
        {/* Header Section */}
        <div style={{ padding: '0 15px', marginBottom: '24px' }}>
          <Row className="align-items-center">
            <Col md={6}>
              <h1 className="page-title mb-0">Manage Tournaments</h1>
            </Col>
            <Col md={6} className="text-end">
              {canCreateTournament && (
                <Button
                  variant="primary"
                  onClick={handleAddNew}
                  className="add-new-btn"
                >
                  <i className="ph-plus me-2"></i>
                  Create Tournament
                </Button>
              )}
            </Col>
          </Row>
        </div>

        {/* Search and Filters Section */}
        <div style={{ padding: '0 15px', marginBottom: '24px' }}>
          <Row className="align-items-center">
            <Col md={6}>
              <Form onSubmit={handleSearch}>
                <div style={{ position: 'relative' }}>
                  <InputGroup>
                    <InputGroup.Text>
                      <i className="ph-magnifying-glass"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ paddingRight: searchTerm ? '35px' : '12px' }}
                    />
                  </InputGroup>
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        color: '#6c757d',
                        fontSize: '16px',
                        zIndex: 2
                      }}
                      title="Clear search"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              </Form>
            </Col>
            <Col md={3}>
              <Select
                classNamePrefix="react-select"
                value={sportsFilter ? { value: sportsFilter, label: sportsFilter } : null}
                onChange={(selectedOption) => {
                  handleFilterChange('sports', selectedOption ? selectedOption.value : '');
                }}
                options={catalogueSportOptions.length
                  ? [{ value: '', label: 'All Sports' }, ...catalogueSportOptions]
                  : sportsOptions}
                placeholder="All Sports"
                isClearable
                maxMenuHeight={200}
                menuPosition="fixed"
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({ 
                    ...base, 
                    minHeight: '38px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }),
                  menu: (base) => ({ 
                    ...base, 
                    zIndex: 99999,
                    maxHeight: 200,
                    overflow: 'hidden'
                  }),
                  menuPortal: (base) => ({ 
                    ...base, 
                    zIndex: 99999 
                  }),
                  menuList: (base) => ({
                    ...base,
                    maxHeight: 200,
                    padding: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }),
                  option: (base, state) => ({
                    ...base,
                    padding: '8px 12px',
                    fontSize: '14px',
                    backgroundColor: state.isFocused ? '#e3f2fd' : 'white',
                    color: state.isFocused ? '#1976d2' : '#212529'
                  })
                }}
              />
            </Col>
            <Col md={3}>
              <Select
                classNamePrefix="react-select"
                value={statusFilter ? { value: statusFilter, label: statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) } : null}
                onChange={(selectedOption) => {
                  handleFilterChange('status', selectedOption ? selectedOption.value : '');
                }}
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'upcoming', label: 'Upcoming' },
                  { value: 'ongoing', label: 'Ongoing' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'cancelled', label: 'Cancelled' }
                ]}
                placeholder="All Status"
                isClearable
                maxMenuHeight={200}
                menuPosition="fixed"
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({ 
                    ...base, 
                    minHeight: '38px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }),
                  menu: (base) => ({ 
                    ...base, 
                    zIndex: 99999,
                    maxHeight: 200,
                    overflow: 'hidden'
                  }),
                  menuPortal: (base) => ({ 
                    ...base, 
                    zIndex: 99999 
                  }),
                  menuList: (base) => ({
                    ...base,
                    maxHeight: 200,
                    padding: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }),
                  option: (base, state) => ({
                    ...base,
                    padding: '8px 12px',
                    fontSize: '14px',
                    backgroundColor: state.isFocused ? '#e3f2fd' : 'white',
                    color: state.isFocused ? '#1976d2' : '#212529'
                  })
                }}
              />
            </Col>
          </Row>
        </div>

        {/* Table Section */}
        <Row>
          <Col>
            {loading || isPageLoading ? (
              <div className="tournament-table-wrapper">
                <ShimmerLoader type="table" />
              </div>
            ) : (
              <ManageTournamentsFixturesTable
                tournaments={tournaments}
                loading={isPageLoading}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                onViewTournament={handleViewTournament}
                onEditTournament={handleEditTournament}
                onDeleteTournament={handleDeleteTournament}
              />
            )}
          </Col>
        </Row>
        <DeleteModal
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          deleteModalFunction={handleConfirmDeleteTournament}
          title="Delete Tournament"
          message={`Are you sure you want to delete "${tournamentToDelete?.name}"? This action cannot be undone.`}
          confirmLabel="Delete Tournament"
          cancelLabel="Cancel"
          isLoading={isDeletingTournament}
          error={deleteError}
        />
      </Container>
    </div>
  );
};

export default ManageTournamentsFixturesPage;
